import { createRoute, useNavigate } from '@tanstack/react-router';
import { Authenticate, layoutRoute } from '@/router/root-routes.tsx';
import { AppAssistanceButton } from '@/components/app/app-assistance-button.tsx';
import { useAuth } from '@/stores/auth.store.ts';
import { usePageProps } from '@/hooks/use-page-props.ts';
import { Button } from '@/components/ui/button.tsx';
import { usePagination } from '@/components/table/use-pagination.ts';
import { useGetProjectsQuery } from '@/app/account/api/queries.ts';
import { useMemo, useState } from 'react';
import { useAppTable } from '@/components/table/use-app-table.ts';
import { ColumnDef } from '@tanstack/react-table';
import { AppTable } from '@/components/table/app-table.tsx';
import { getImgOrNoPreview, TProject } from '@/types/models.ts';
import { ProjectEdit } from '@/app/account/projects/project-edit.tsx';

export const projectsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: 'account/projects',
  component: ProjectsPage,
  beforeLoad: Authenticate(),
});

const chunkSize = 3;
export function ProjectItemsCell({ project }: { project: TProject }) {
  const chunk = [];
  for (let i = 0; i < chunkSize && i < project.items.length; i++) {
    chunk.push(project.items[i]);
  }
  return (
    <div className="flex flex-wrap justify-center gap-2">
      {chunk.map((item) => (
        <div key={item.id} title={item.name}>
          <img className="w-12 h-12 object-cover" src={getImgOrNoPreview(item.img_url)} alt={item.name} />
        </div>
      ))}
      {project.items.length > chunkSize && (
        <div className="flex items-center justify-center w-12 h-12 bg-gray-100 border">
          +{project.items.length - chunkSize}
        </div>
      )}
    </div>
  );
}

function ProjectsColumns(): ColumnDef<TProject>[] {
  return [
    { header: 'Project Name', accessorKey: 'name', meta: { cellClassName: 'text-xl' } },
    { header: 'Created', accessorKey: 'created_at', size: 150 },
    {
      header: 'Items',
      cell: ({ row }) => <ProjectItemsCell project={row.original} />,
      meta: { className: 'text-center' },
    },
  ];
}

function ProjectsPage() {
  usePageProps({
    title: 'Projects',
  });
  const { user } = useAuth();
  const pagination = usePagination();
  const query = useGetProjectsQuery({ ...pagination.params });
  const columns = useMemo(() => ProjectsColumns(), []);
  const at = useAppTable({
    query,
    columns,
    pagination,
  });
  const [showNewProject, setShowNewProject] = useState(false);
  const navigate = useNavigate();
  const onRowClick = (project: TProject) => {
    // noinspection JSIgnoredPromiseFromCall
    navigate({ to: '/account/projects/$id', params: { id: String(project.id) } });
  };
  return (
    <div className="max-w-8xl mx-auto py-4">
      <div className="grid lg:grid-cols-3 mb-4">
        <div>
          <Button className="uppercase" onClick={() => setShowNewProject(true)}>
            New Project
          </Button>
        </div>
        <div className="text-center">
          <h1>PROJECTS</h1>
        </div>
        <div className="flex lg:justify-end">
          <AppAssistanceButton
            title="New Project"
            message="I am looking for help with a new project."
            subject={`New project assistance request from ${user.full_name}`}
          />
        </div>
      </div>
      <div className="max-w-4xl mx-auto">
        <AppTable at={at} onRowClick={onRowClick} />
      </div>
      {showNewProject && (
        <ProjectEdit onClose={() => setShowNewProject(false)} />
      )}
    </div>
  );
}
